import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import HomeBanner from '../HomeBanner';
import HomeFeedUICard from '../HomeFeedUICard';

import HomeFeedCarousel from '../HomeFeedCarousel';
import { Grid, Cell, CellFullWidth } from '../HomeFeedGrid';
import getSafe from '../../utils/getSafe';
import { getTargetURL } from '../../utils/home/factories';
import { SHOW_HOME_MAP_VIEW_ACTION } from '../../queries/home/constants';
import { SUPPORTED_BLOCKS, SUPPORTED_LAYOUTS, PRELOAD_LIMIT } from '../../utils/home/constants';

import trackBeforeRedirect from '../../utils/home/trackBeforeRedirect';

import styles from './styles.scss';

export const HomeFeedList = ({
  result,
  loading,
  handleBannerClick,
  trackTappedLayoutBlock,
  getPreloadCount,
}) =>
  result.blocks
    .map((block) => {
      if (block.typeName === SUPPORTED_BLOCKS.CARD) {
        const url = getTargetURL(block.target);
        const preloadCount = getPreloadCount();
        return (
          <Cell key={block.key}>
            <div className={styles.box}>
              <HomeFeedUICard
                {...block}
                url={url}
                isPlaceholder={loading}
                preLoad={preloadCount <= PRELOAD_LIMIT}
                onClick={(e) => {
                  trackBeforeRedirect(e, url, () => {
                    trackTappedLayoutBlock({
                      layoutTrackingId: result.trackingId,
                      blockTrackingId: block.trackingId,
                    });
                  });
                }}
              />
            </div>
          </Cell>
        );
      }

      if (block.typeName === SUPPORTED_BLOCKS.BANNER) {
        // If we add other URLs to the response that are not restaurants
        // they will need to be handled here. We need to pass them down here
        // in order to render the component as an <a> rather than a <button>
        const restaurantUrl = getSafe(() => block.target.restaurant.links.self.href);
        const isHomeMapViewBanner =
          getSafe(() => block.target.action) === SHOW_HOME_MAP_VIEW_ACTION;
        return (
          <CellFullWidth key={block.key}>
            <div className={styles.box}>
              <HomeBanner
                heading={block.header}
                caption={block.caption}
                buttonCaption={block.buttonCaption}
                type={block.theme}
                images={block.images}
                imageSize={isHomeMapViewBanner ? 'large' : 'medium'}
                href={restaurantUrl}
                onClick={
                  block.target
                    ? (e) =>
                        handleBannerClick({
                          target: block.target,
                          event: e,
                          layoutTrackingId: result.trackingId,
                          blockTrackingId: block.trackingId,
                        })
                    : undefined
                }
                isPlaceholder={loading}
              />
            </div>
          </CellFullWidth>
        );
      }
      return null;
    })
    .filter(Boolean);

const HomeFeed = ({
  results,
  loading,
  handleGetCollection,
  handleBannerClick,
  trackTappedLayoutBlock,
  trackTappedLayout,
}) => {
  const preloadCount = useRef(0);
  const getPreloadCount = () => {
    const current = preloadCount.current;
    preloadCount.current++;
    return current;
  };

  // Reset the ref and preload count everytime the loading state changes.
  useEffect(
    () => {
      preloadCount.current = 0;
    },
    [loading],
  );

  return (
    <Grid>
      {results
        .map((result) => {
          if (result.typeName === SUPPORTED_LAYOUTS.LIST) {
            return (
              <HomeFeedList
                key={result.key}
                result={result}
                loading={loading}
                handleBannerClick={handleBannerClick}
                trackTappedLayoutBlock={trackTappedLayoutBlock}
                trackTappedLayout={trackTappedLayout}
                getPreloadCount={getPreloadCount}
              />
            );
          }
          if (result.typeName === SUPPORTED_LAYOUTS.CAROUSEL) {
            return (
              <CellFullWidth key={result.key}>
                <div className={styles.box}>
                  <HomeFeedCarousel
                    result={result}
                    handleGetCollection={handleGetCollection}
                    loading={loading}
                    trackTappedLayoutBlock={trackTappedLayoutBlock}
                    trackTappedLayout={trackTappedLayout}
                    getPreloadCount={getPreloadCount}
                  />
                </div>
              </CellFullWidth>
            );
          }
          return null;
        })
        .filter(Boolean)}
    </Grid>
  );
};

HomeFeed.propTypes = {
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  handleGetCollection: PropTypes.func.isRequired,
  handleBannerClick: PropTypes.func.isRequired,
  trackTappedLayoutBlock: PropTypes.func.isRequired,
  trackTappedLayout: PropTypes.func.isRequired,
};

HomeFeed.defaultProps = {
  loading: false,
};

HomeFeedList.propTypes = {
  result: PropTypes.shape({
    header: PropTypes.string,
    key: PropTypes.string.isRequired,
    typeName: PropTypes.oneOf([SUPPORTED_LAYOUTS.LIST, SUPPORTED_LAYOUTS.CAROUSEL]).isRequired,
    blocks: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
  loading: PropTypes.bool,
  handleBannerClick: PropTypes.func.isRequired,
  trackTappedLayoutBlock: PropTypes.func.isRequired,
  getPreloadCount: PropTypes.func,
};

HomeFeedList.defaultProps = {
  getPreloadCount: () => {},
};

export default HomeFeed;
